import React, { useCallback, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { debounce } from "lodash";
import "./McKessonPageHeader.scss";

interface ActionButton {
  label: string;
  onClick: () => void;
}

interface McKessonPageHeaderProps {
  title: string;
  disRecords: number;
  totalRecords: number;
  searchVal: { searchText: string; active?: boolean | null };
  setSearchVal: (val: any) => void;
  defaultFilter: { searchText: string; active?: boolean | null };
  actionButtons?: ActionButton[];
  checkboxLabel?: string;
  showCheckbox?: boolean;
  clearButtonText?: string;
  showClearButton?: boolean;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  itemId?: string;
  onItemIdChange?: (val: string) => void;
  itemIdError?: string;
}

const McKessonPageHeader: React.FC<McKessonPageHeaderProps> = ({
  title,
  disRecords,
  totalRecords,
  searchVal,
  setSearchVal,
  defaultFilter,
  actionButtons = [],
  checkboxLabel = "Active",
  showCheckbox = false,
  clearButtonText = "Clear All / Refresh",
  showClearButton = true,
  pageSize,
  onPageSizeChange,
  itemId,
  onItemIdChange,
  itemIdError,
}) => {
  const [localSearchText, setLocalSearchText] = useState(
    searchVal?.searchText ?? ""
  );
  const [localItemId, setLocalItemId] = useState(itemId ?? "");
  const [localPageSize, setLocalPageSize] = useState(
    pageSize?.toString() ?? "100"
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchVal({ ...searchVal, searchText: value });
      }, 500),
    [searchVal, setSearchVal]
  );

  const debouncedItemId = useMemo(
    () =>
      debounce((value: string) => {
        onItemIdChange?.(value);
      }, 500),
    [onItemIdChange]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalSearchText(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleItemIdChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalItemId(value);
      debouncedItemId(value);
    },
    [debouncedItemId]
  );

  const handleActiveChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchVal({
        ...searchVal,
        active: e.target.checked ? true : null,
      });
    },
    [searchVal, setSearchVal]
  );

  const handleClearAll = useCallback(() => {
    setLocalSearchText("");
    setLocalItemId("");
    onItemIdChange?.("");
    setSearchVal({ ...defaultFilter });
  }, [defaultFilter, setSearchVal, onItemIdChange]);

  return (
    <div className="mckesson-page-header">
      <div className="listing-title-info">
        <h2>{title}</h2>
        <p>
          Displaying {disRecords} of {totalRecords} Results
        </p>
      </div>
      <div className="mckesson-search-controls">
        <div className="listing-searchbar-item-box">
          <input
            type="text"
            min={0}
            className="form-control"
            placeholder="Search"
            onChange={handleSearchChange}
            value={localSearchText}
            autoFocus
            onFocus={(e) => e.target.select()}
          />
        </div>
        {onItemIdChange !== undefined && (
          <div className="listing-searchbar-item-box">
            <input
              type="number"
              className={`form-control${itemIdError ? " is-invalid" : ""}`}
              placeholder="Item Id"
              value={localItemId}
              onChange={handleItemIdChange}
            />
            {itemIdError && (
              <div className="invalid-feedback d-block">{itemIdError}</div>
            )}
          </div>
        )}
        {showCheckbox && (
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="checkbox"
              checked={searchVal?.active ?? false}
              onChange={handleActiveChange}
            />
            <label className="form-check-label">{checkboxLabel}</label>
          </div>
        )}
        {actionButtons.map((btn, index) => (
          <Button
            key={index}
            type="button"
            variant="primary"
            onClick={btn.onClick}
          >
            {btn.label}
          </Button>
        ))}
        {showClearButton && (
          <Button
            type="button"
            variant="outline-secondary"
            className="btn-search"
            onClick={handleClearAll}
          >
            {clearButtonText}
          </Button>
        )}
      </div>
      {pageSize !== undefined && onPageSizeChange && (
        <div className="mckesson-page-size">
          <label htmlFor="mcPageSize">Page Size:</label>
          <input
            id="mcPageSize"
            type="number"
            className="form-control"
            value={localPageSize}
            onChange={(e) => {
              const raw = e.target.value;
              setLocalPageSize(raw);
              const val = parseInt(raw, 10);
              if (val > 0) onPageSizeChange(val);
            }}
            onBlur={() => {
              const val = parseInt(localPageSize, 10);
              if (!val || val <= 0) {
                setLocalPageSize("100");
                onPageSizeChange(100);
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export default McKessonPageHeader;
